import React, { useState, useEffect } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import BarChart from './BarChart';
import PieChart from './PieChart';
import { elements } from './elements';
import { CHART_TYPES, dateKeyMapping } from './utils';
import { DownOutlined } from '@ant-design/icons';
const ResponsiveGridLayout = WidthProvider(Responsive);

// Helper function to convert backend size to grid size
const getGridSize = (backendSize) => {
  const size = parseInt(backendSize);
  if (size == 4) {
    return { w: 12, h: 3 };
  } else if (size == 2) {
    return { w: 6, h: 2 }; 
  } else {
    return { w: 3, h: 2 }; 
  }
};

const ChartTypeSelect = ({ value, onChange }) => {
  return (
    <div className="select-wrapper">
      <select 
        value={value} 
        onChange={(e) => onChange(e.target.value)}
        className="chart-type-select"
      >
        <option value="barchart">Bar Chart</option>
        <option value="piechart">Pie Chart</option>
      </select>
      <i className="icon icon-dropdown_caret" />
    </div>
  );
};

const ChartCard = ({ title, data, visible, defaultChartType, onSizeChange }) => {
  const [selectedChartType, setSelectedChartType] = useState(defaultChartType.value);

  if (!visible || !data || data.length === 0) return null;

  return (
    <div className="chart-card">
      <div className="chart-card__header">
        <div className="drag-handle">⠿</div>
        <h2 className="chart-card__title">{title}</h2>
        <ChartTypeSelect 
          value={selectedChartType}
          onChange={setSelectedChartType}
        />
      </div>
      <div className="chart-card__content">
        <div className="chart-card__container">
          {selectedChartType === "barchart" ?
            <BarChart data={data} height={300} /> :
            <PieChart data={data} height={300} />
          }
        </div>
      </div>
    </div>
  );
};

const DashboardCharts = ({ data, initialLayout, saveLayoutToBackend, sortBy, setSelectedFormData}) => {
  const [layouts, setLayouts] = useState(null);
  const [currentBreakpoint, setCurrentBreakpoint] = useState('lg');
  const [originalData, setOriginalData] = useState(data);

  const handleSort = () => {
    if(sortBy === "none"){
      setSelectedFormData(originalData);
      return;
    }
    if (sortBy === "ascending") {
      const sortedData = data?.map(section => ({
        ...section,
        data: [...section.data].sort((a, b) => a.total_data_points - b.total_data_points),
      }));
      setSelectedFormData(sortedData);
      
    } else if (sortBy === "descending") {
      const sortedData = data?.map(section => ({
        ...section,
        data: [...section.data].sort((a, b) => b.total_data_points - a.total_data_points),
      }));
      setSelectedFormData(sortedData);
    }
  }
  useEffect(() => {
    handleSort();
  }, [sortBy]);

  useEffect(() => {
    let currentRow = 0;
    let currentCol = 0;
  
    const layoutItems = initialLayout || (data?.map(item => ({
      dash_name: item.title?.replace("_", " "),
      dash_size: "2" 
    })))?.flatMap(item => {
      const additionalItems = [];
    
      for (const [key, suffix] of Object.entries(dateKeyMapping)) {
        if (item.dash_name.includes(suffix)) {
          const baseName = item.dash_name.replace(suffix, '').trim();
    
          Object.values(dateKeyMapping).forEach((otherSuffix) => {
            if (otherSuffix !== suffix) {
              const newName = `${baseName} ${otherSuffix}`;
              additionalItems.push({
                dash_name: newName,
                dash_size: item.dash_size
              });
            }
          });
        }
      }
    
      return [item, ...additionalItems];
    });
  
    const gridLayout = layoutItems?.map((item) => {
      const { w, h } = getGridSize(item.dash_size);
  
      if (currentCol + w > 12) {
        currentCol = 0;
        currentRow += 4;
      }
  
      const layoutItem = {
        i: item.dash_name,
        x: currentCol,
        y: currentRow,
        w: w,
        h: h,
        minW: 3,
        maxW: 12
      };
  
      currentCol += w;
  
      return layoutItem;
    });
  
    setLayouts({
      lg: gridLayout,
      md: gridLayout.map(item => ({
        ...item,
        w: Math.min(10, item.w),
        x: item.w >= 10 ? 0 : item.x
      })),
      sm: gridLayout.map(item => ({
        ...item,
        w: 6,
        x: 0
      }))
    });
  }, [initialLayout, data]);

  if (!data || data.length === 0) {
    return <div className="no-data">No Data</div>;
  }

  const totalDataPointsSum = data[0]?.data?.reduce(
    (sum, item) => sum + item.total_data_points,
    0
  );

  const handleLayoutChange = (layout, layouts) => {
    setLayouts(layouts);
    const backendLayout = [];
    const orderedLayout = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);

    const existingKeys = new Set(orderedLayout.map((item) => item.i));
  
    orderedLayout.forEach((item) => {
      backendLayout.push({
        dash_name: item.i,
        dash_size: item.w >= 12 ? "4" : item.w >= 6 ? "2" : "1"
      });
  
      for (const [key, suffix] of Object.entries(dateKeyMapping)) {
        if (item.i.includes(suffix)) {
          const baseName = item.i.replace(suffix, '').trim();
  
          Object.values(dateKeyMapping).forEach((otherSuffix) => {
            if (otherSuffix !== suffix) {
              const newName = `${baseName} ${otherSuffix}`;
              if (!existingKeys.has(newName)) {
                backendLayout.push({
                  dash_name: newName,
                  dash_size: item.w >= 12 ? "4" : item.w >= 6 ? "2" : "1"
                });
                existingKeys.add(newName);
              }
            }
          });
        }
      }
    });
    saveLayoutToBackend(backendLayout);
  };

  return (
    <div className="dashboard">
      <div className="dashboard__total-points">
        <h4>Total Data Points - <span>{totalDataPointsSum}</span></h4>
      </div>
      {layouts && (
        <ResponsiveGridLayout
          className="dashboard__charts"
          layouts={layouts}
          breakpoints={{ lg: 1200, md: 996, sm: 768 }}
          cols={{ lg: 12, md: 10, sm: 6 }}
          rowHeight={150}
          onLayoutChange={handleLayoutChange}
          onBreakpointChange={setCurrentBreakpoint}
          isDraggable={true}
          isResizable={true}
          containerPadding={[16, 16]}
          margin={[16, 16]}
          draggableHandle=".drag-handle"
        >
          {data?.map((element, index) => {
            const defaultChartType = (
              element?.title === "Risk stats for screened people" ||
              element?.title.toLowerCase().includes("designation")
            ) ? CHART_TYPES.PIE_CHART : CHART_TYPES.BAR_CHART;

            return (
              <div key={element?.title?.replace("_", " ")}>
                <ChartCard
                  title={element?.title?.replace("_", " ")}
                  data={element?.data}
                  visible={element?.data?.length > 0}
                  defaultChartType={defaultChartType}
                />
              </div>
            );
          })}
        </ResponsiveGridLayout>
      )}
    </div>
  );
};

export default DashboardCharts;